import React from 'react';
import { Area, CartesianGrid, ComposedChart, Line, ReferenceArea, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import InfoTip from '../../shared/InfoTip';
import { fmtCurrency, fmtNumber } from '../../shared/format';
import ThemedChartTooltip, { ThemedTooltipBox } from '../../shared/ThemedChartTooltip';
import { SkeletonChart } from '../../shared/Skeleton';
import { ErrorState } from '../../shared/StateMessage';
import { fmtTrendDate, dateToTs, findTrendGaps, timeAxisProps } from '../../shared/trendAxis';

// Memoized: the parent (GoogleAdsPage) re-renders whenever the campaign filters
// fold into / out of the sticky header on scroll. Without memo, each of those
// re-renders rebuilds chartData (new array ref) and Recharts replays its entry
// animation, so the chart appears to "reload". Props only change on a real
// data fetch, so memoizing skips the scroll-driven re-renders.
const SpendTrend = React.memo(({ spendTrend, loading, error }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3 font-semibold inline-flex items-center">
      Spend &amp; Conversions Trend
      <InfoTip text="Daily ad spend overlaid with daily conversions for the range. Spend rising without conversions following is the pattern to watch." />
    </div>
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sm:p-6">
      {error ? (
        <ErrorState message={error} />
      ) : loading ? (
        <SkeletonChart height={280} />
      ) : !spendTrend || spendTrend.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-gray-400 dark:text-gray-500 text-sm">
          No performance data available for this date range.
        </div>
      ) : (() => {
        const totalCost = spendTrend.reduce((sum, datum) => sum + datum.cost, 0);
        const totalConv = spendTrend.reduce((sum, datum) => sum + datum.all_conversions, 0);
        const avgCost = totalCost / spendTrend.length;
        const chartData = spendTrend.map((datum) => ({
          ...datum,
          ts: dateToTs(datum.date),
          dateLabel: fmtTrendDate(datum.date),
        }));
        const gaps = findTrendGaps(chartData);

        return (
          <div className="rotate-x-labels">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-0.5 bg-blue-500 rounded-full inline-block"></span>
                <span className="text-xs text-gray-600 dark:text-gray-400">Cost ($)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-0.5 bg-emerald-500 rounded-full inline-block"></span>
                <span className="text-xs text-gray-600 dark:text-gray-400">All Conversions</span>
              </div>
              <div className="lg:ml-auto flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                <span>Total Spend: <span className="font-semibold text-gray-700 dark:text-gray-200">{fmtCurrency(totalCost)}</span></span>
                <span>Total Conversions: <span className="font-semibold text-gray-700 dark:text-gray-200">{fmtNumber(totalConv, true)}</span></span>
                <span>Avg Daily Spend: <span className="font-semibold text-gray-700 dark:text-gray-200">{fmtCurrency(avgCost)}</span></span>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="costAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                {/* Shade each missing-data range; the line bridges it, the band explains
                    it (native SVG <title> on hover). Bound to the left axis since the
                    dual-axis chart needs an explicit yAxisId on the band. */}
                {gaps.map((gap) => (
                  <ReferenceArea
                    key={gap.x1}
                    yAxisId="left"
                    x1={gap.x1}
                    x2={gap.x2}
                    fill="#9ca3af"
                    fillOpacity={0.12}
                    ifOverflow="extendDomain"
                    label={{ value: 'no data', fontSize: 10, fill: '#9ca3af', position: 'insideTop' }}
                  >
                    <title>{gap.label}</title>
                  </ReferenceArea>
                ))}
                <XAxis {...timeAxisProps()} />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 10, fill: '#3B82F6' }}
                  tickFormatter={(value) => `$${value}`}
                  axisLine={false}
                  tickLine={false}
                  width={50}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 10, fill: '#10B981' }}
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  width={35}
                />
                <ThemedChartTooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const datum = payload[0].payload;
                    return (
                      <ThemedTooltipBox title={datum.dateLabel}>
                        <div>Cost: {fmtCurrency(datum.cost)}</div>
                        <div>Conversions: {fmtNumber(datum.all_conversions, true)}</div>
                        <div>Clicks: {datum.clicks.toLocaleString()}</div>
                      </ThemedTooltipBox>
                    );
                  }}
                />
                <Area yAxisId="left" type="monotone" dataKey="cost" fill="url(#costAreaGrad)" stroke="none" />
                <Line yAxisId="left" type="monotone" dataKey="cost" stroke="#3B82F6" strokeWidth={2} dot={chartData.length <= 31} activeDot={{ r: 4 }} />
                <Line yAxisId="right" type="monotone" dataKey="all_conversions" stroke="#10B981" strokeWidth={2} dot={false} strokeDasharray="5 3" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        );
      })()}
    </div>
  </div>
));

SpendTrend.displayName = 'SpendTrend';

export default SpendTrend;
